import React, { useState, useRef, useEffect } from 'react';
import { Card, Button, Form, Alert, Badge, Spinner, Row, Col } from 'react-bootstrap';
import { FaMagic, FaPaperPlane, FaRocket, FaRedo, FaPlay, FaBrain, FaWrench, FaExclamationTriangle, FaPlusCircle } from 'react-icons/fa';
import axios from 'axios';
import { API_URL } from '../config';

/**
 * WorkflowWizard - Conversational workflow planner.
 *
 * Takes a user description, asks ~2 follow-up questions, then suggests
 * existing deployed workflows or proposes new agent chains.
 *
 * Props:
 *   - onRunSupervisor(prompt)          → run the automatic supervisor with a refined prompt
 *   - onBuildManualWorkflow(agents)    → switch to manual mode & populate agent chain
 *   - onRunExistingWorkflow(workflowId) → run an existing deployed workflow
 *   - onCreateAgent({ purpose, name }) → switch to Agent Creator tab with pre-filled data
 *   - designContext                     → { howMade, whatToMake } from the Design Config form
 */
const WorkflowWizard = ({ onRunSupervisor, onBuildManualWorkflow, onRunExistingWorkflow, onCreateAgent, designContext }) => {
  // Wizard conversation
  const [conversation, setConversation] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState(null);

  // Suggestions from the wizard
  const [suggestions, setSuggestions] = useState(null);
  const [summaryMessage, setSummaryMessage] = useState('');
  const [noMatch, setNoMatch] = useState(null); // When no agents can handle the task

  // Has the user started the conversation?
  const [started, setStarted] = useState(false);

  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation, suggestions]);

  const handleSend = async () => {
    const text = userInput.trim();
    if (!text || isThinking) return;

    setError(null);
    setUserInput('');

    // If this is the first message and we have design context, prepend it
    let messageToSend = text;
    if (!started && designContext) {
      const parts = [];
      if (designContext.howMade) parts.push(`Manufacturing method: ${designContext.howMade}`);
      if (designContext.whatToMake) parts.push(`Project: ${designContext.whatToMake}`);
      if (parts.length > 0) {
        messageToSend = `${parts.join('. ')}. ${text}`;
      }
    }

    // Add user message to conversation
    const newConversation = [
      ...conversation,
      { role: 'user', content: messageToSend, display: text }
    ];
    setConversation(newConversation);
    setStarted(true);
    setIsThinking(true);

    try {
      const response = await axios.post(`${API_URL}/api/workflow-wizard/suggest`, {
        message: messageToSend,
        conversation: newConversation.map(m => ({ role: m.role, content: m.content })),
      });

      if (!response.data.success) {
        setError(response.data.error || 'Wizard failed');
        setIsThinking(false);
        return;
      }

      const result = response.data;

      if (result.type === 'questions') {
        // AI wants to ask follow-up questions
        const questionsText = result.questions.map((q, i) => `${i + 1}. ${q}`).join('\n');
        const assistantMsg = `${result.message || ''}\n\n${questionsText}`;

        setConversation(prev => [
          ...prev,
          { role: 'assistant', content: JSON.stringify(result), display: assistantMsg }
        ]);
      } else if (result.type === 'no_match') {
        // No suitable agents for this task
        setNoMatch(result);
        setConversation(prev => [
          ...prev,
          { role: 'assistant', content: JSON.stringify(result), display: result.message || "I couldn't find suitable agents for this task." }
        ]);
      } else if (result.type === 'suggestions') {
        // AI has suggestions ready
        setSuggestions(result.suggestions || []);
        setSummaryMessage(result.message || '');

        setConversation(prev => [
          ...prev,
          { role: 'assistant', content: JSON.stringify(result), display: result.message || 'Here are my recommendations:' }
        ]);

        // If there's a supervisor_prompt, store it for later
        if (result.supervisor_prompt) {
          setSuggestions(prev => 
            (prev || []).map(s => 
              s.type === 'supervisor' ? { ...s, supervisor_prompt: result.supervisor_prompt } : s
            )
          );
        }
      }
    } catch (err) {
      console.error('Wizard error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to get suggestions');
    } finally {
      setIsThinking(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    if (suggestion.type === 'existing_workflow' && suggestion.workflow_id) {
      onRunExistingWorkflow?.(suggestion.workflow_id);
    } else if (suggestion.type === 'agent_chain' && suggestion.agents?.length > 0) {
      onBuildManualWorkflow?.(suggestion.agents);
    } else if (suggestion.type === 'supervisor') {
      // Use the refined prompt or generate one from the conversation
      const prompt = suggestion.supervisor_prompt 
        || suggestion.description 
        || conversation.filter(m => m.role === 'user').map(m => m.display || m.content).join('. ');
      onRunSupervisor?.(prompt);
    }
  };

  const handleReset = () => {
    setConversation([]);
    setSuggestions(null);
    setNoMatch(null);
    setSummaryMessage('');
    setStarted(false);
    setError(null);
    setUserInput('');
  };

  const getSuggestionIcon = (type) => {
    switch (type) {
      case 'existing_workflow': return <FaRocket className="me-1" />;
      case 'agent_chain': return <FaWrench className="me-1" />;
      case 'supervisor': return <FaBrain className="me-1" />;
      default: return <FaPlay className="me-1" />;
    }
  };

  const getSuggestionBadge = (type) => {
    switch (type) {
      case 'existing_workflow': return <Badge bg="success" className="ms-2">Ready to Use</Badge>;
      case 'agent_chain': return <Badge bg="primary" className="ms-2">Agent Chain</Badge>;
      case 'supervisor': return <Badge bg="info" className="ms-2">AI Supervised</Badge>;
      default: return null;
    }
  };

  return (
    <div>
      {/* Wizard Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0">
            <FaMagic className="me-2" />
            Workflow Wizard
          </h5>
          <p className="text-muted mb-0 small">
            Describe what you want to create and I'll suggest the best workflow
          </p>
        </div>
        {started && (
          <Button variant="outline-secondary" size="sm" onClick={handleReset}>
            <FaRedo className="me-1" /> Start Over
          </Button>
        )}
      </div>

      {/* Conversation */}
      <Card className="mb-3" style={{ maxHeight: '400px', overflow: 'hidden' }}>
        <Card.Body style={{ maxHeight: '360px', overflowY: 'auto', padding: '16px' }}>
          {!started && (
            <Alert variant="light" className="border text-center py-4">
              <FaMagic size={24} className="text-primary mb-2 d-block mx-auto" />
              <strong>What would you like to create?</strong>
              <p className="text-muted small mb-0 mt-1">
                Describe your project below and I'll help you find the right workflow.
                I'll ask a couple questions, then suggest the best approach.
              </p>
            </Alert>
          )}

          {conversation.map((msg, idx) => (
            <div
              key={idx}
              className={`mb-3 d-flex ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
            >
              <div
                style={{
                  maxWidth: '80%',
                  padding: '10px 14px',
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  backgroundColor: msg.role === 'user' ? '#0d6efd' : '#f0f2f5',
                  color: msg.role === 'user' ? '#fff' : '#212529',
                  fontSize: '0.9rem',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {msg.display || msg.content}
              </div>
            </div>
          ))}

          {isThinking && (
            <div className="mb-3 d-flex justify-content-start">
              <div
                style={{
                  padding: '10px 14px',
                  borderRadius: '16px 16px 16px 4px',
                  backgroundColor: '#f0f2f5',
                  fontSize: '0.9rem',
                }}
              >
                <Spinner animation="border" size="sm" className="me-2" />
                Thinking...
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </Card.Body>
      </Card>

      {/* Error */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          {error}
        </Alert>
      )}

      {/* Suggestions */}
      {suggestions && suggestions.length > 0 && (
        <Card className="mb-3 border-primary">
          <Card.Header className="bg-primary text-white">
            <strong>
              <FaMagic className="me-2" />
              Recommended Workflows
            </strong>
            {summaryMessage && (
              <div className="small mt-1 opacity-75">{summaryMessage}</div>
            )}
          </Card.Header>
          <Card.Body className="p-0">
            <Row className="g-0">
              {suggestions.map((s, idx) => (
                <Col key={idx} md={suggestions.length === 1 ? 12 : 6} lg={suggestions.length <= 2 ? 6 : 4}>
                  <div
                    className="p-3 h-100"
                    style={{
                      borderRight: idx < suggestions.length - 1 ? '1px solid #dee2e6' : 'none',
                    }}
                  >
                    <div className="d-flex align-items-center mb-2">
                      <h6 className="mb-0">{s.name}</h6>
                      {getSuggestionBadge(s.type)}
                    </div>

                    <p className="text-muted small mb-2">{s.description}</p>

                    {s.agents && s.agents.length > 0 && (
                      <div className="mb-2">
                        <div className="d-flex flex-wrap gap-1 align-items-center">
                          {s.agents.map((agent, ai) => (
                            <React.Fragment key={ai}>
                              <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>
                                {agent}
                              </Badge>
                              {ai < s.agents.length - 1 && (
                                <span className="text-muted" style={{ fontSize: '0.7rem' }}>→</span>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    )}

                    {s.output_format && (
                      <div className="small text-muted mb-2">
                        <strong>Output:</strong> {s.output_format}
                      </div>
                    )}

                    {s.reasoning && (
                      <div className="small text-muted mb-3 fst-italic">
                        {s.reasoning}
                      </div>
                    )}

                    <Button
                      variant={s.type === 'existing_workflow' ? 'success' : s.type === 'supervisor' ? 'info' : 'primary'}
                      size="sm"
                      className="w-100"
                      onClick={() => handleSelectSuggestion(s)}
                    >
                      {getSuggestionIcon(s.type)}
                      {s.type === 'existing_workflow' ? 'Use This Workflow' :
                       s.type === 'supervisor' ? 'Run with AI Supervisor' :
                       'Build This Chain'}
                    </Button>
                  </div>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* No Match - when no agents can handle the task */}
      {noMatch && (
        <Card className="mb-3 border-warning">
          <Card.Header className="bg-warning text-dark">
            <strong>
              <FaExclamationTriangle className="me-2" />
              No Matching Agents Found
            </strong>
          </Card.Header>
          <Card.Body>
            <p className="mb-2">{noMatch.message}</p>

            {noMatch.missing_capabilities && noMatch.missing_capabilities.length > 0 && (
              <div className="mb-3">
                <strong className="small">What's needed:</strong>
                <ul className="mb-0 small">
                  {noMatch.missing_capabilities.map((cap, i) => (
                    <li key={i}>{cap}</li>
                  ))}
                </ul>
              </div>
            )}

            {noMatch.closest_agents && noMatch.closest_agents.length > 0 && (
              <div className="mb-3">
                <strong className="small">Closest existing agents:</strong>
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {noMatch.closest_agents.map((agent, i) => (
                    <Badge key={i} bg="secondary" style={{ fontSize: '0.75rem' }}>{agent}</Badge>
                  ))}
                </div>
              </div>
            )}

            {noMatch.suggestion && (
              <Alert variant="info" className="mb-2 py-2 small">
                <FaPlusCircle className="me-1" />
                {noMatch.suggestion}
              </Alert>
            )}

            <div className="d-flex gap-2">
              {onCreateAgent && (
                <Button 
                  variant="primary" 
                  size="sm" 
                  onClick={() => {
                    // Build a rich purpose from the wizard conversation + no_match info
                    const userMessages = conversation
                      .filter(m => m.role === 'user')
                      .map(m => m.display || m.content)
                      .join('. ');
                    const capabilities = (noMatch.missing_capabilities || []).join('; ');
                    const purpose = `${userMessages}${capabilities ? `\n\nRequired capabilities: ${capabilities}` : ''}`;
                    const name = '';
                    onCreateAgent({ purpose, name });
                  }}
                >
                  <FaPlusCircle className="me-1" /> Create This Agent
                </Button>
              )}
              <Button variant="outline-warning" size="sm" onClick={handleReset}>
                <FaRedo className="me-1" /> Try Different Request
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Input */}
      <Form onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
        <Form.Group>
          <div className="d-flex gap-2">
            <Form.Control
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              placeholder={
                !started
                  ? "e.g., I want to make a tactile graphic of a dolphin for a visually impaired student..."
                  : suggestions
                    ? "Ask for different options or refine your request..."
                    : noMatch
                      ? "Try a different request, or describe what you'd like differently..."
                      : "Answer the questions above..."
              }
              disabled={isThinking}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <Button
              type="submit"
              variant="primary"
              disabled={isThinking || !userInput.trim()}
            >
              {isThinking ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <FaPaperPlane />
              )}
            </Button>
          </div>
          <Form.Text className="text-muted">
            {!started
              ? "Describe what you want to create — I'll ask a couple questions, then suggest the best approach"
              : suggestions
                ? "You can pick a suggestion above, or ask me to refine the options"
                : noMatch
                  ? "No agents matched — try a different request or create a new agent in the Agent Creator"
                  : "Press Enter to send"
            }
          </Form.Text>
        </Form.Group>
      </Form>
    </div>
  );
};

export default WorkflowWizard;
